import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import T from '../styles/theme';
import {Avatar, Badge, ProgressBar, Select , Checkbox} from '../components/UI';
import Icon from '../components/Icon';
import { useStore, useFieldOptions, fetchAllPaginated } from '../data/store';
import supabase from '../data/supabase';
import AddContactModal from '../components/contacts/AddContactModal';
import { matchesFilter } from '../data/listFilters';

// ─── FILTER DEFINITIONS ─────────────────────────
const cfDate = (d) => { if (!d) return "—"; const dt = new Date(d); const m = String(dt.getMonth()+1).padStart(2,"0"); const dy = String(dt.getDate()).padStart(2,"0"); const y = String(dt.getFullYear()).slice(-2); return m+"/"+dy+"/"+y; };

// Standard truncation triple — applied to every text-bearing <td> in the
// contacts table so paragraph-length values (long names, multi-line company
// strings, Apollo/SalesNav custom field descriptions) clip to one line with
// ellipsis instead of inflating row heights. Display-only — DB values are
// untouched and accessible via the contact record page on click.
const truncStyle = { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
// Module-level skeleton — Tier B fields (status/source) carry empty options
// so the component can overlay the live override-aware options via
// useFieldOptions inside ContactsPage. applyFilter only reads `type`, never
// `options`, so the placeholder is safe for the pure-function path.
const FILTER_FIELDS = [
  { id: "status", label: "Status", type: "select", options: [] },
  { id: "source", label: "Source", type: "select", options: [] },
  { id: "owner", label: "Contact Owner", type: "owner" },
  { id: "score_min", label: "Score (min)", type: "number" },
  { id: "score_max", label: "Score (max)", type: "number" },
  { id: "company", label: "Company", type: "text" },
  { id: "title", label: "Job Title", type: "text" },
  { id: "industry", label: "Industry", type: "text" },
  { id: "city", label: "City", type: "text" },
  { id: "state", label: "State", type: "text" },
  { id: "tags", label: "Tags (contains)", type: "text" },
  { id: "created_after", label: "Created After", type: "date" },
  { id: "created_before", label: "Created Before", type: "date" },
  { id: "last_activity_after", label: "Last Activity After", type: "date" },
  { id: "last_activity_before", label: "Last Activity Before", type: "date" },
  { id: "has_phone", label: "Has Phone Number", type: "boolean" },
  { id: "has_email", label: "Has Email", type: "boolean" },
];

const DEFAULT_VIEWS = [
  { id: "all", name: "All Contacts", filters: [], icon: null, system: true },
  { id: "hot_leads", name: "Hot Leads", filters: [{ field: "score_min", value: "75" }, { field: "status", value: "active" }], icon: null },
  { id: "needs_follow_up", name: "Needs Follow-up", filters: [{ field: "last_activity_before", value: new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10) }], icon: null },
  { id: "no_phone", name: "Missing Phone", filters: [{ field: "has_phone", value: "false" }], icon: null },
  { id: "outbound", name: "Outbound Leads", filters: [{ field: "source", value: "Outbound" }], icon: null },
  { id: "inbound", name: "Inbound Leads", filters: [{ field: "source", value: "Inbound" }], icon: null },
];

// ─── FILTER LOGIC ────────────────────────────────
function applyFilter(contact, f) {
  const { field, value } = f;
  if (!value && value !== false) return true;
  const v = String(value).toLowerCase();

  switch (field) {
    case "status": return (contact.status || "").toLowerCase() === v;
    case "source": return (contact.source || "").toLowerCase() === v;
    case "owner": return (contact.ownerId || contact.owner || "") === value;
    case "score_min": return (contact.score || 0) >= Number(value);
    case "score_max": return (contact.score || 0) <= Number(value);
    case "company": return (contact.company || contact.co || "").toLowerCase().includes(v);
    case "title": return (contact.title || "").toLowerCase().includes(v);
    case "industry": return (contact.industry || "").toLowerCase().includes(v);
    case "city": return (contact.city || "").toLowerCase().includes(v);
    case "state": return (contact.state || "").toLowerCase().includes(v);
    case "tags": return (contact.tags || []).some(t => t.toLowerCase().includes(v));
    case "created_after": return (contact.created || "") >= value;
    case "created_before": return (contact.created || "") <= value;
    case "last_activity_after": return (contact.lastActivity || contact.last_activity_at || "") >= value;
    case "last_activity_before": return (contact.lastActivity || contact.last_activity_at || "") <= value;
    case "has_phone": return value === "true" ? !!(contact.ph || contact.phone) : !(contact.ph || contact.phone);
    case "has_email": return value === "true" ? !!contact.email : !contact.email;
    default: return true;
  }
}

function applyAllFilters(contacts, filters) {
  if (!filters || filters.length === 0) return contacts;
  return contacts.filter(c => filters.every(f => applyFilter(c, f)));
}

// ─── FILTER ROW COMPONENT ────────────────────────
// fieldDefs is the dynamic, override-aware version built inside ContactsPage
// via useFieldOptions; falls back to the module-level FILTER_FIELDS skeleton.
function FilterRow({ filter, onChange, onRemove, teamMembers, fieldDefs }) {
  const fields = fieldDefs || FILTER_FIELDS;
  const fieldDef = fields.find(f => f.id === filter.field) || {};
  const selStyle = { width: 200, borderRadius: 8 };
  const inputStyle = { padding: "8px 12px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "rgba(255,255,255,0.06)", color: T.txt, fontSize: 12, outline: "none", fontFamily: "inherit", width: 200 };

  return (
    <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
      <Select value={filter.field} onChange={e => onChange({ ...filter, field: e.target.value, value: "" })} style={selStyle}>
        <option value="">Select field...</option>
        {fields.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
      </Select>

      {fieldDef.type === "select" && (
        <Select value={filter.value} onChange={e => onChange({ ...filter, value: e.target.value })} style={selStyle}>
          <option value="">Any</option>
          {fieldDef.options.map(o => <option key={o} value={o}>{o}</option>)}
        </Select>
      )}
      {fieldDef.type === "owner" && (
        <Select value={filter.value} onChange={e => onChange({ ...filter, value: e.target.value })} style={selStyle}>
          <option value="">Any owner</option>
          {(teamMembers || []).map(m => <option key={m._dbId || m.id} value={m._dbId || m.id}>{m.name}</option>)}
        </Select>
      )}
      {fieldDef.type === "text" && (
        <input value={filter.value} onChange={e => onChange({ ...filter, value: e.target.value })} placeholder={"Contains..."} style={inputStyle} />
      )}
      {fieldDef.type === "number" && (
        <input type="number" value={filter.value} onChange={e => onChange({ ...filter, value: e.target.value })} placeholder="Value" style={inputStyle} />
      )}
      {fieldDef.type === "date" && (
        <input type="date" value={filter.value} onChange={e => onChange({ ...filter, value: e.target.value })} style={inputStyle} />
      )}
      {fieldDef.type === "boolean" && (
        <Select value={filter.value} onChange={e => onChange({ ...filter, value: e.target.value })} placeholder="— Select —" options={[{value:"true",label:"Yes"},{value:"false",label:"No"}]} style={selStyle} />
      )}

      <button onClick={onRemove} style={{ width: 28, height: 28, borderRadius: 8, background: "rgba(255,255,255,0.08)", border: "1px solid rgba(217,219,221,0.12)", cursor: "pointer", color: T.tx3, fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>×</button>
    </div>
  );
}

// ─── MAIN COMPONENT ──────────────────────────────
export default function ContactsPage({ onSelectContact, isMobile }) {
  const store = useStore();
  // Tier B options — reactive to admin-saved overrides. Overlay onto the
  // module-level FILTER_FIELDS skeleton so FilterRow gets live options.
  const statusOptions = useFieldOptions("contacts", "status");
  const sourceOptions = useFieldOptions("contacts", "source");
  const dynFilterFields = useMemo(() => FILTER_FIELDS.map(f => {
    if (f.id === "status") return { ...f, options: statusOptions };
    if (f.id === "source") return { ...f, options: sourceOptions };
    return f;
  }), [statusOptions, sourceOptions]);
  const [search, setSearch] = useState("");
  const [viewMode, setViewModeRaw] = useState(() => {
    try { return localStorage.getItem("cf_contacts_viewMode") || "list"; } catch { return "list"; }
  });
  const setViewMode = (m) => { setViewModeRaw(m); try { localStorage.setItem("cf_contacts_viewMode", m); } catch {} };
  const [sortCol, setSortCol] = useState("score");
  const [sortDir, setSortDir] = useState("desc");
  const [selected, setSelected] = useState(new Set());
  const [showAddContactModal, setShowAddContactModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Smart Views from store (Supabase-backed)
  const [activeViewId, setActiveViewIdRaw] = useState(() => {
    try { return localStorage.getItem("cf_contacts_activeView") || "all"; } catch { return "all"; }
  });
  const setActiveViewId = (id) => { setActiveViewIdRaw(id); try { localStorage.setItem("cf_contacts_activeView", id); } catch {} };
  const [filters, setFilters] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [showSaveView, setShowSaveView] = useState(false);
  const [shareView, setShareView] = useState(false);
  const [saveViewError, setSaveViewError] = useState("");
  const [newViewName, setNewViewName] = useState("");
  const [queueMsg, setQueueMsg] = useState("");
  const [showViewPicker, setShowViewPicker] = useState(false);
  const [viewSearch, setViewSearch] = useState("");
  const viewPickerRef = useRef(null);

  // ── Lists multi-select filter ──
  // Distinct from saved-views: this filters the People table by org-scoped
  // contact_lists membership. Static lists are fetched on demand
  // (per-list) when their id is selected; smart lists are evaluated
  // client-side via the shared matchesFilter from src/data/listFilters.
  const [allLists, setAllLists] = useState([]);
  const [selectedListIds, setSelectedListIdsRaw] = useState(() => {
    try {
      const v = localStorage.getItem("cf_contacts_listFilter");
      return v ? JSON.parse(v) : [];
    } catch { return []; }
  });
  const setSelectedListIds = (ids) => {
    setSelectedListIdsRaw(ids);
    try { localStorage.setItem("cf_contacts_listFilter", JSON.stringify(ids)); } catch {}
  };
  const [staticListMembers, setStaticListMembers] = useState({}); // { list_id: Set<contact_id> }
  const [listsDropdownOpen, setListsDropdownOpen] = useState(false);
  const listsDropdownRef = useRef(null);

  // ── Disposition filters (commit D of dispositions feature) ──
  // Three quick-filter chips for the most common behavior queries plus a
  // multiselect dropdown that lists every admin-configured disposition
  // dynamically from store.callDispositions. Selections persist via
  // localStorage to mirror the existing list-filter persistence pattern.
  const [showDncOnly, setShowDncOnlyRaw] = useState(() => {
    try { return localStorage.getItem("cf_contacts_dncOnly") === "1"; } catch { return false; }
  });
  const [showQualifiedOnly, setShowQualifiedOnlyRaw] = useState(() => {
    try { return localStorage.getItem("cf_contacts_qualifiedOnly") === "1"; } catch { return false; }
  });
  const [showRequeueOnly, setShowRequeueOnlyRaw] = useState(() => {
    try { return localStorage.getItem("cf_contacts_requeueOnly") === "1"; } catch { return false; }
  });
  const [selectedDispositionIds, setSelectedDispositionIdsRaw] = useState(() => {
    try {
      const v = localStorage.getItem("cf_contacts_selectedDispositionIds");
      return v ? JSON.parse(v) : [];
    } catch { return []; }
  });
  const setShowDncOnly = (v) => { setShowDncOnlyRaw(v); try { localStorage.setItem("cf_contacts_dncOnly", v ? "1" : "0"); } catch {} };
  const setShowQualifiedOnly = (v) => { setShowQualifiedOnlyRaw(v); try { localStorage.setItem("cf_contacts_qualifiedOnly", v ? "1" : "0"); } catch {} };
  const setShowRequeueOnly = (v) => { setShowRequeueOnlyRaw(v); try { localStorage.setItem("cf_contacts_requeueOnly", v ? "1" : "0"); } catch {} };
  const setSelectedDispositionIds = (ids) => {
    setSelectedDispositionIdsRaw(ids);
    try { localStorage.setItem("cf_contacts_selectedDispositionIds", JSON.stringify(ids)); } catch {}
  };
  const [dispositionDropdownOpen, setDispositionDropdownOpen] = useState(false);
  const dispositionDropdownRef = useRef(null);
  // Click-outside dismiss for disposition dropdown — mirrors lists pattern.
  useEffect(() => {
    if (!dispositionDropdownOpen) return;
    const onDoc = (e) => {
      if (dispositionDropdownRef.current && !dispositionDropdownRef.current.contains(e.target)) {
        setDispositionDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [dispositionDropdownOpen]);

  // ── Column editor ──
  const customContactFields = useMemo(() => {
    const fields = store.customFields?.contacts || [];
    if (fields.length > 0) console.log("[CoreFlow] Contact custom fields for column editor:", fields.map(f => f.label));
    return fields;
  }, [store.customFields]);
  const ALL_COLUMNS = useMemo(() => [
    // Widths are now explicit on every column. Previously `name`, `company`,
    // `email`, and `title` were `null` (auto-sized), which combined with
    // table-layout: auto allowed long values to blow out row heights. Under
    // table-layout: fixed (added below) the column width is enforced and
    // the truncStyle on body cells clips overflow with ellipsis.
    { id: "name", label: "Contact", w: 220, required: true },
    { id: "company", label: "Company", w: 180 },
    { id: "email", label: "Email", w: 220 },
    { id: "phone", label: "Phone", w: 130 },
    { id: "title", label: "Title", w: 160 },
    { id: "score", label: "Score", w: 100 },
    { id: "status", label: "Status", w: 120 },
    { id: "source", label: "Source", w: 120 },
    { id: "last", label: "Last Activity", w: 110 },
    { id: "owner", label: "Owner", w: 110 },
    { id: "created", label: "Created", w: 110 },
    { id: "tags", label: "Tags", w: 120 },
    ...customContactFields.map(cf => ({ id: "cf_" + cf.id, label: cf.label || cf.name, w: 120 })),
  ], [customContactFields]);
  // Lookup by colId → width (px). Body <td>s read this so widths are
  // enforced at the cell level under table-layout: fixed (header widths
  // alone aren't sufficient under fixed layout when body cells override).
  const colWidthById = useMemo(() => {
    const m = {};
    ALL_COLUMNS.forEach(c => { if (c.w) m[c.id] = c.w; });
    return m;
  }, [ALL_COLUMNS]);
  const DEFAULT_COLS = ["name", "company", "score", "status", "source", "last"];
  const [visibleCols, setVisibleCols] = useState(() => {
    try { const saved = localStorage.getItem("cf_contact_columns"); return saved ? JSON.parse(saved) : DEFAULT_COLS; }
    catch { return DEFAULT_COLS; }
  });
  // Filter visibleCols to known column ids before rendering. Defends against:
  //   • Stale cf_<id> entries from custom fields that were deleted (the id is
  //     persisted in user_preferences but no longer exists in ALL_COLUMNS).
  //   • Race during initial load where store.customFields hasn't arrived yet,
  //     so cf_<id> entries are momentarily absent from ALL_COLUMNS.
  // Both header and row iterate this same filtered array — guarantees their
  // <th> and <td> counts can never diverge, which was the root cause of the
  // "data appears under wrong header after reorder" alignment bug.
  const renderCols = useMemo(
    () => visibleCols.filter(id => ALL_COLUMNS.some(c => c.id === id)),
    [visibleCols, ALL_COLUMNS]
  );
  const [showColEditor, setShowColEditor] = useState(false);
  const [colEditorDraft, setColEditorDraft] = useState(() => {
    try { const saved = localStorage.getItem("cf_contact_columns"); return saved ? JSON.parse(saved) : DEFAULT_COLS; }
    catch { return DEFAULT_COLS; }
  });
  const dragItem = useRef(null);
  const dragOver = useRef(null);
  const [colSearch, setColSearch] = useState("");

  // Load column config from Supabase on mount
  useEffect(() => {
    if (!store.currentUserId) return;
    supabase.from("user_preferences").select("value")
      .eq("user_id", store.currentUserId).eq("key", "contact_columns").maybeSingle()
      .then(({ data }) => {
        if (data?.value && Array.isArray(data.value)) {
          setVisibleCols(data.value);
          setColEditorDraft(data.value);
          localStorage.setItem("cf_contact_columns", JSON.stringify(data.value));
        }
      });
  }, [store.currentUserId]);

  useEffect(() => {
    const h = (e) => { if (viewPickerRef.current && !viewPickerRef.current.contains(e.target)) setShowViewPicker(false); };
    if (showViewPicker) document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [showViewPicker]);

  // ── Lists filter: fetch all org lists once on mount/orgId change ──
  // Both static and smart lists are loaded so the dropdown can show the
  // full set. Smart lists carry their `filters` JSONB inline so we can
  // evaluate them client-side via matchesFilter.
  useEffect(() => {
    const orgId = store.currentOrgId || window.__coreflow_org_id;
    if (!orgId) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchAllPaginated(() =>
          supabase
            .from("contact_lists")
            .select("id, name, type, filters")
            .eq("org_id", orgId)
            .order("created_at", { ascending: true })
        );
        if (!cancelled) setAllLists(data || []);
      } catch (e) {
        console.warn("[ContactsList] fetch lists failed:", e?.message);
      }
    })();
    return () => { cancelled = true; };
  }, [store.currentOrgId]);

  // ── Lists filter: fetch static-list memberships when selection changes ──
  // Only fires for lists of type='static' — smart lists are evaluated
  // in-memory via matchesFilter and don't need a membership fetch.
  useEffect(() => {
    if (!selectedListIds || selectedListIds.length === 0) {
      setStaticListMembers({});
      return;
    }
    const staticIds = selectedListIds.filter(id => {
      const l = allLists.find(x => x.id === id);
      return l && l.type === "static";
    });
    if (staticIds.length === 0) {
      setStaticListMembers({});
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const next = {};
        // Sequential per-list fetch keeps the membership-by-list shape clear
        // and lets each list paginate cleanly via fetchAllPaginated. Order
        // by added_at so .range() pagination has stable boundaries.
        for (const lid of staticIds) {
          const rows = await fetchAllPaginated(() =>
            supabase
              .from("contact_list_members")
              .select("contact_id")
              .eq("list_id", lid)
              .order("added_at", { ascending: true })
          );
          if (cancelled) return;
          next[lid] = new Set((rows || []).map(r => r.contact_id));
        }
        if (!cancelled) setStaticListMembers(next);
      } catch (e) {
        console.warn("[ContactsList] fetch list members failed:", e?.message);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedListIds, allLists, store.currentOrgId]);

  // ── Lists filter: click-outside-to-close dropdown ──
  useEffect(() => {
    if (!listsDropdownOpen) return;
    const handler = (e) => {
      if (listsDropdownRef.current && !listsDropdownRef.current.contains(e.target)) {
        setListsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [listsDropdownOpen]);

  // Save columns to both localStorage and Supabase.
  // NOTE: cols is saved verbatim, not filtered against ALL_COLUMNS. A previous
  // version (ab685ef5) added a write-time "cleaned = cols.filter(id => ALL_COLUMNS
  // .some(c => c.id === id))" cleanup intended to garbage-collect ghost cf_<id>
  // entries from deleted custom fields. That backfired: during the auth-boot
  // race where store.customFields.contacts hasn't loaded yet, ALL_COLUMNS has
  // zero cf_ entries, so the filter classified EVERY valid cf_ id as a ghost
  // and permanently destroyed user_preferences. Now we trust the input and
  // rely on the render-time renderCols memo to hide true ghosts at display.
  // Render-time filtering is reactive (recovers when customFields loads); the
  // write path must NOT prune ids based on incomplete state.
  const applyColumns = (cols) => {
    setVisibleCols(cols);
    try { localStorage.setItem("cf_contact_columns", JSON.stringify(cols)); } catch {}
    if (store.currentUserId) {
      supabase.from("user_preferences").upsert({
        user_id: store.currentUserId,
        org_id: store.currentOrgId,
        key: "contact_columns",
        value: cols,
        updated_at: new Date().toISOString(),
      }, { onConflict: "user_id,key" }).then(({ error }) => {
        if (error) console.warn("[CoreFlow] Column save error:", error.message);
      });
    }
  };

  // Combine default views with user's saved views from Supabase
  const views = useMemo(() => [...DEFAULT_VIEWS, ...store.savedViews], [store.savedViews]);

  const [viewKey, setViewKey] = useState(0);
  const [hasRestoredView, setHasRestoredView] = useState(false);

  // Restore persisted view filters on mount (once savedViews are loaded)
  useEffect(() => {
    if (hasRestoredView) return;
    if (activeViewId === "all") { setHasRestoredView(true); return; }
    const allViews = [...DEFAULT_VIEWS, ...store.savedViews];
    const view = allViews.find(v => v.id === activeViewId);
    if (view) {
      setFilters(view.filters ? view.filters.map(f => ({ ...f })) : []);
      if (view.columns && Array.isArray(view.columns) && view.columns.length > 0) {
        setVisibleCols(view.columns);
        setColEditorDraft(view.columns);
      }
      setHasRestoredView(true);
    } else if (store.savedViews.length > 0 || !store.loading) {
      // savedViews loaded but view not found — fall back to all
      setActiveViewId("all");
      setHasRestoredView(true);
    }
  }, [activeViewId, store.savedViews, store.loading, hasRestoredView]);

  // Load view filters when switching views
  const switchView = useCallback((viewId) => {
    setActiveViewId(viewId);
    const view = [...DEFAULT_VIEWS, ...store.savedViews].find(v => v.id === viewId) || DEFAULT_VIEWS[0];
    setFilters(view.filters ? view.filters.map(f => ({ ...f })) : []);
    // Apply view columns if it's a shared view with saved columns
    if (view.columns && Array.isArray(view.columns) && view.columns.length > 0) {
      setVisibleCols(view.columns);
      setColEditorDraft(view.columns);
    }
    setSelected(new Set());
    setCurrentPage(1);
    setViewKey(k => k + 1);
  }, [store.savedViews]);

  // Save custom view to Supabase
  const saveViewHandler = async () => {
    if (!newViewName.trim()) return;
    const name = newViewName.trim();
    const duplicate = views.find(v => v.name.toLowerCase() === name.toLowerCase());
    if (duplicate) {
      setSaveViewError("A view named \"" + name + "\" already exists");
      return;
    }
    setSaveViewError("");
    // Include columns for shared views so everyone gets the same layout
    const viewData = { name, filters: filters.map(f => ({ ...f })), icon: null, isShared: shareView };
    if (shareView) viewData.columns = [...visibleCols];
    await store.saveView(viewData);
    setNewViewName("");
    setShareView(false);
    setShowSaveView(false);
    setShowFilters(false);
  };

  const deleteViewHandler = (viewId) => {
    store.deleteView(viewId);
    if (activeViewId === viewId) switchView("all");
  };

  // Filter + sort contacts
  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("desc"); }
  };

  const filtered = useMemo(() => {
    let l = store.contacts.slice();
    // Apply search
    if (search) l = l.filter(c => (c.fn + " " + c.ln + " " + (c.company || c.co) + " " + c.email + " " + c.title).toLowerCase().includes(search.toLowerCase()));
    // Apply smart view filters
    l = applyAllFilters(l, filters);
    // Apply Lists multi-select filter (OR across selected lists, AND with view filters above).
    // A contact passes if it's in ANY of the selected lists. Static lists are
    // matched via the staticListMembers map (fetched on selection change);
    // smart lists are evaluated client-side via matchesFilter against the
    // list's `filters` JSONB.
    if (selectedListIds && selectedListIds.length > 0) {
      const matchByList = (contact) => {
        for (const lid of selectedListIds) {
          const list = allLists.find(x => x.id === lid);
          if (!list) continue;
          if (list.type === "static") {
            const memberSet = staticListMembers[lid];
            if (memberSet && memberSet.has(contact.id)) return true;
          } else if (list.type === "smart") {
            const lf = list.filters || [];
            if (lf.length === 0) return true; // empty smart list = matches all
            if (lf.every(f => matchesFilter(contact, f))) return true;
          }
        }
        return false;
      };
      l = l.filter(matchByList);
    }
    // Disposition filter chips + multiselect (commit D). Each is independent
    // AND-combined with the others; multiple chips active narrows the result.
    if (showDncOnly) l = l.filter(c => !!c.do_not_call);
    if (showQualifiedOnly) l = l.filter(c => !!c.qualified_at);
    if (showRequeueOnly) {
      const now = Date.now();
      l = l.filter(c => c.requeue_after && new Date(c.requeue_after).getTime() > now);
    }
    if (selectedDispositionIds && selectedDispositionIds.length > 0) {
      const idSet = new Set(selectedDispositionIds);
      l = l.filter(c => c.last_disposition_id && idSet.has(c.last_disposition_id));
    }
    // Sort
    l.sort((a, b) => {
      const av = sortCol === "name" ? (a.fn + a.ln) : sortCol === "company" ? (a.company || a.co) : sortCol === "score" ? a.score : sortCol === "status" ? a.status : sortCol === "source" ? a.source : a.last;
      const bv = sortCol === "name" ? (b.fn + b.ln) : sortCol === "company" ? (b.company || b.co) : sortCol === "score" ? b.score : sortCol === "status" ? b.status : sortCol === "source" ? b.source : b.last;
      if (typeof av === "number") return sortDir === "asc" ? av - bv : bv - av;
      return sortDir === "asc" ? String(av || "").localeCompare(String(bv || "")) : String(bv || "").localeCompare(String(av || ""));
    });
    return l;
  }, [search, store.contacts, filters, sortCol, sortDir, selectedListIds, allLists, staticListMembers, showDncOnly, showQualifiedOnly, showRequeueOnly, selectedDispositionIds]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paginatedContacts = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, currentPage, pageSize]);

  // Selection
  const allSelected = paginatedContacts.length > 0 && paginatedContacts.every(c => selected.has(c.id));
  const toggleAll = () => { if (allSelected) setSelected(new Set()); else setSelected(new Set(paginatedContacts.map(c => c.id))); };
  const toggleOne = (id) => { setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; }); };

  // Add to call queue via Supabase
  const addToQueue = async (contactIds) => {
    const ids = Array.from(contactIds);
    const existing = store.callQueue.map(q => q.ct);
    const newEntries = ids.filter(id => !existing.includes(id)).map((id, i) => {
      const c = store.contacts.find(x => x.id === id);
      return {
        id: Date.now() + i,
        ct: id,
        reason: "Added from Smart View",
        pri: (c?.score || 0) >= 75 ? "hot" : (c?.score || 0) >= 50 ? "warm" : "cold",
        attempts: 0,
        best: "",
      };
    });
    if (newEntries.length > 0) {
      const result = await store.addToQueue(newEntries);
      const added = result?.added ?? newEntries.length;
      const skipped = result?.skipped ?? 0;
      let msg;
      if (added === 0 && skipped > 0) {
        msg = `0 added, ${skipped} skipped (already queued, DNC, qualified, or awaiting requeue)`;
      } else if (skipped > 0) {
        msg = `Added ${added} contact${added > 1 ? "s" : ""}, ${skipped} skipped (DNC, qualified, or awaiting requeue)`;
      } else {
        msg = `Added ${added} contact${added > 1 ? "s" : ""} to call queue`;
      }
      setQueueMsg(msg);
      setTimeout(() => setQueueMsg(""), 4000);
    } else {
      setQueueMsg("All selected contacts are already in the queue");
      setTimeout(() => setQueueMsg(""), 3000);
    }
    setSelected(new Set());
  };

  const bulkDelete = () => {
    if (selected.size === 0) return;
    if (!window.confirm("Delete " + selected.size + " contacts?")) return;
    selected.forEach(id => store.deleteContact(id));
    setSelected(new Set());
  };

  const sortIcon = (col) => sortCol === col ? (sortDir === "asc" ? " ▲" : " ▼") : "";
  const activeView = views.find(v => v.id === activeViewId) || views[0];
  const crd = { background: "transparent", border: "none", borderRadius: 0, boxShadow: "none" };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", background: "transparent", fontFamily: "inherit" }}>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* ─── VIEW DROPDOWN ─── */}
      <div style={{ padding: isMobile ? "10px 12px 0" : "14px 24px 0", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0, flexWrap: isMobile ? "wrap" : "nowrap", gap: isMobile ? 8 : 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div ref={viewPickerRef} style={{ position: "relative" }}>
            <button onClick={() => { setShowViewPicker(!showViewPicker); setViewSearch(""); }} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 12px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: showViewPicker ? "rgba(255,255,255,0.08)" : "transparent", cursor: "pointer", fontFamily: "inherit", transition: "all .15s ease" }} onMouseEnter={e => { if (!showViewPicker) e.currentTarget.style.background = "rgba(255,255,255,0.04)"; }} onMouseLeave={e => { if (!showViewPicker) e.currentTarget.style.background = "transparent"; }}>
              <Icon name="user" size={14} color="#FAFAFA" />
              <span style={{ fontSize: 15, fontWeight: 400, color: "#FAFAFA" }}>{activeView.name}</span>
              <span style={{ fontSize: 10, color: "#FAFAFA", opacity: 0.5, marginLeft: 2 }}>▾</span>
            </button>
            {showViewPicker && <div className="cf-dropdown-popup" style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, width: 300, background: "#33363D", border: "1px solid rgba(217,219,221,0.12)", borderRadius: 8, boxShadow: "0 24px 64px rgba(0,0,0,0.80)", zIndex: 99999, overflow: "hidden" }}>
              <div style={{ padding: "12px 14px 10px", borderBottom: "1px solid rgba(217,219,221,0.08)" }}>
                <input autoFocus value={viewSearch} onChange={e => setViewSearch(e.target.value)} placeholder="Search views..." style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.10)", background: "rgba(255,255,255,0.04)", color: "#FAFAFA", fontSize: 13, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }} />
              </div>
              <div style={{ maxHeight: 320, overflowY: "auto", padding: "8px 10px" }}>
                {views.filter(v => !viewSearch || v.name.toLowerCase().includes(viewSearch.toLowerCase())).map(v => {
                  const canDelete = !v.system && !DEFAULT_VIEWS.find(d => d.id === v.id);
                  return (
                  <div key={v.id} style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 4 }}>
                    <div onClick={() => { switchView(v.id); setShowViewPicker(false); }} style={{ display: "flex", alignItems: "center", flex: 1, padding: "11px 14px", borderRadius: 8, cursor: "pointer", background: activeViewId === v.id ? "#4059AD" : "rgba(255,255,255,0.04)", color: activeViewId === v.id ? "#fff" : "#FAFAFA" }} onMouseEnter={e => { if (activeViewId !== v.id) e.currentTarget.style.background = "rgba(255,255,255,0.08)"; }} onMouseLeave={e => { if (activeViewId !== v.id) e.currentTarget.style.background = "rgba(255,255,255,0.04)"; }}>
                      <span style={{ fontSize: 13, fontWeight: activeViewId === v.id ? 600 : 400 }}>{v.name}</span>
                    </div>
                    {canDelete ? <div onClick={() => deleteViewHandler(v.id)} style={{ width: 26, height: 26, borderRadius: 7, background: "rgba(255,255,255,0.04)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: "#FAFAFA", opacity: 0.4, flexShrink: 0 }} onMouseEnter={e => { e.currentTarget.style.opacity = "1"; e.currentTarget.style.color = "#EF4444"; }} onMouseLeave={e => { e.currentTarget.style.opacity = "0.4"; e.currentTarget.style.color = "#FAFAFA"; }}>×</div> : <div style={{ width: 26, flexShrink: 0 }} />}
                  </div>);
                })}
                <div style={{ borderTop: "1px solid rgba(217,219,221,0.08)", marginTop: 6, paddingTop: 6 }}>
                  <div onClick={() => { setFilters([{ field: "", value: "" }]); setShowFilters(true); setActiveViewId("custom"); setShowViewPicker(false); }} style={{ padding: "10px 14px", borderRadius: 8, cursor: "pointer", color: "#4059AD", fontSize: 13, fontWeight: 400 }} onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.04)"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>+ New View</div>
                </div>
              </div>
            </div>}
          </div>
          <span style={{ fontSize: 12, color: "#FAFAFA", opacity: 0.5 }}>{filtered.length} contacts</span>
          {filters.length > 0 && <>
            <button onClick={() => setShowFilters(true)} style={{ padding: "5px 14px", borderRadius: 8, fontSize: 10, fontWeight: 400, letterSpacing: ".04em", textTransform: "uppercase", color: T.txt, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(217,219,221,0.12)", cursor: "pointer", fontFamily: "inherit" }}>Edit</button>
            <button onClick={() => { setFilters([]); setShowFilters(false); switchView("all"); }} style={{ padding: "5px 14px", borderRadius: 8, fontSize: 10, fontWeight: 400, letterSpacing: ".04em", textTransform: "uppercase", color: T.txt, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(217,219,221,0.12)", cursor: "pointer", fontFamily: "inherit" }}>Clear All</button>
          </>}
        </div>
        <div style={{ display: "flex", gap: 2 }}>
          <button onClick={() => setViewMode("list")} style={{ padding: "7px 14px", borderRadius: "8px 0 0 8px", border: "1px solid rgba(217,219,221,0.12)", background: viewMode === "list" ? "#4059AD" : "rgba(255,255,255,0.06)", color: viewMode === "list" ? "#fff" : T.tx3, fontSize: 10, fontWeight: 400, cursor: "pointer", fontFamily: "inherit" }}>List</button>
          <button onClick={() => setViewMode("board")} style={{ padding: "7px 14px", borderRadius: "0 8px 8px 0", border: "1px solid rgba(217,219,221,0.12)", borderLeft: "none", background: viewMode === "board" ? "#4059AD" : "rgba(255,255,255,0.06)", color: viewMode === "board" ? "#fff" : T.tx3, fontSize: 10, fontWeight: 400, cursor: "pointer", fontFamily: "inherit" }}>Board</button>
        </div>
      </div>

      {/* ─── FILTER MODAL ─── */}
      {showFilters && ReactDOM.createPortal(
        <div style={{ position: "fixed", inset: 0, zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div onClick={() => setShowFilters(false)} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.50)", backdropFilter: "blur(4px)" }} />
          <div style={{ position: "relative", width: 520, maxWidth: "92vw", background: "#2C2E33", border: "1px solid rgba(217,219,221,0.12)", borderRadius: 8, boxShadow: "0 40px 100px rgba(0,0,0,0.40)", padding: "20px 24px" }}>
            {/* Header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <span style={{ fontSize: 14, fontWeight: 400, color: T.txt }}>Filters</span>
              <button onClick={() => setShowFilters(false)} style={{ width: 28, height: 28, borderRadius: 8, background: "rgba(255,255,255,0.08)", border: "1px solid rgba(217,219,221,0.12)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, color: T.tx3 }}>×</button>
            </div>

            {/* Filter rows */}
            {filters.map((f, i) => (
              <FilterRow key={i} filter={f} teamMembers={store.teamMembers} fieldDefs={dynFilterFields}
                onChange={(updated) => setFilters(prev => prev.map((x, j) => j === i ? updated : x))}
                onRemove={() => setFilters(prev => prev.filter((_, j) => j !== i))} />
            ))}

            {/* Actions */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12, paddingTop: 12, borderTop: "1px solid rgba(217,219,221,0.08)" }}>
              <button onClick={() => setFilters(prev => [...prev, { field: "", value: "" }])}
                style={{ padding: "8px 18px", borderRadius: 8, fontSize: 11, fontWeight: 400, letterSpacing: ".04em", textTransform: "uppercase", color: "#D9DBDD", background: T.blue, border: "none", cursor: "pointer", fontFamily: "inherit" }}>
                + Add Filter
              </button>
              <div style={{ display: "flex", gap: 6 }}>
                {filters.some(f => f.field) && (
                  <button onClick={() => { setSaveViewError(""); setShowSaveView(true); }} style={{ padding: "8px 18px", borderRadius: 8, fontSize: 11, fontWeight: 400, letterSpacing: ".04em", textTransform: "uppercase", color: "#D9DBDD", background: T.blue, border: "none", cursor: "pointer", fontFamily: "inherit" }}>
                    Save as View
                  </button>
                )}
                <button onClick={() => setShowFilters(false)} style={{ padding: "8px 18px", borderRadius: 8, fontSize: 11, fontWeight: 400, letterSpacing: ".04em", textTransform: "uppercase", color: T.txt, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(217,219,221,0.12)", cursor: "pointer", fontFamily: "inherit" }}>
                  Done
                </button>
              </div>
            </div>

            {/* Save as View nested popup */}
            {showSaveView && (
              <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10, borderRadius: 8, overflow: "hidden" }}>
                <div onClick={() => setShowSaveView(false)} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.60)" }} />
                <div style={{ position: "relative", width: 340, background: "#2C2E33", border: "1px solid rgba(217,219,221,0.12)", borderRadius: 8, padding: "20px 24px", boxShadow: "0 20px 60px rgba(0,0,0,0.40)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                    <span style={{ fontSize: 14, fontWeight: 400, color: T.txt }}>Save as View</span>
                    <button onClick={() => setShowSaveView(false)} style={{ width: 28, height: 28, borderRadius: 8, background: "rgba(255,255,255,0.08)", border: "1px solid rgba(217,219,221,0.12)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, color: T.tx3 }}>×</button>
                  </div>
                  <input value={newViewName} onChange={e => { setNewViewName(e.target.value); setSaveViewError(""); }} placeholder="View name..."
                    onKeyDown={e => e.key === "Enter" && saveViewHandler()} autoFocus
                    style={{ width: "100%", padding: "10px 14px", borderRadius: 8, border: "1px solid " + (saveViewError ? "rgba(239,68,68,0.5)" : "rgba(217,219,221,0.12)"), background: "rgba(255,255,255,0.06)", color: T.txt, fontSize: 13, outline: "none", fontFamily: "inherit", marginBottom: saveViewError ? 6 : 12, boxSizing: "border-box" }} />
                  {saveViewError && <div style={{ fontSize: 11, color: T.red, marginBottom: 10 }}>{saveViewError}</div>}
                  <div onClick={() => setShareView(!shareView)} style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", marginBottom: 14, padding: "8px 0" }}>
                    <div style={{ width: 20, height: 20, borderRadius: 8, background: shareView ? "#4059AD" : "transparent", border: shareView ? "1.5px solid #4059AD" : "1.5px solid rgba(217,219,221,0.12)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all 0.15s" }}>
                      {shareView && <span style={{ color: "#fff", fontSize: 11, fontWeight: 400 }}>✓</span>}
                    </div>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 400, color: T.txt }}>Share with team</div>
                      <div style={{ fontSize: 10, color: T.tx3 }}>Everyone in your org will see this view</div>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                    <button onClick={() => setShowSaveView(false)} style={{ padding: "8px 18px", borderRadius: 8, fontSize: 11, fontWeight: 400, letterSpacing: ".04em", textTransform: "uppercase", color: T.txt, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(217,219,221,0.12)", cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
                    <button onClick={saveViewHandler} style={{ padding: "8px 18px", borderRadius: 8, fontSize: 11, fontWeight: 400, letterSpacing: ".04em", textTransform: "uppercase", color: "#D9DBDD", background: T.blue, border: "none", cursor: "pointer", fontFamily: "inherit" }}>Save</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>, document.body
      )}

      {/* ─── COLUMN EDITOR MODAL ─── */}
      {showColEditor && ReactDOM.createPortal(
        <div style={{ position: "fixed", inset: 0, zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div onClick={() => setShowColEditor(false)} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.50)", backdropFilter: "blur(4px)" }} />
          <div style={{ position: "relative", width: 700, maxWidth: "94vw", height: 520, maxHeight: "80vh", background: "#2C2E33", border: "1px solid rgba(217,219,221,0.12)", borderRadius: 8, boxShadow: "0 40px 100px rgba(0,0,0,0.40)", display: "flex", flexDirection: "column", overflow: "hidden" }}>
            {/* Header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 20px", borderBottom: "1px solid rgba(217,219,221,0.08)", flexShrink: 0 }}>
              <span style={{ fontSize: 14, fontWeight: 400, color: T.txt }}>Edit Columns</span>
              <button onClick={() => setShowColEditor(false)} style={{ width: 28, height: 28, borderRadius: 8, background: "rgba(255,255,255,0.08)", border: "1px solid rgba(217,219,221,0.12)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, color: T.tx3 }}>×</button>
            </div>

            {/* Two-panel body */}
            <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
              {/* Left: Available fields */}
              <div style={{ width: "50%", borderRight: "1px solid rgba(217,219,221,0.08)", display: "flex", flexDirection: "column", overflow: "hidden" }}>
                <div style={{ padding: "12px 16px 8px", flexShrink: 0 }}>
                  <div style={{ fontSize: 10, fontWeight: 400, color: T.tx3, letterSpacing: ".06em", marginBottom: 8 }}>AVAILABLE FIELDS</div>
                  <input value={colSearch} onChange={e => setColSearch(e.target.value)} placeholder="Search fields..."
                    style={{ width: "100%", padding: "7px 10px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "rgba(255,255,255,0.06)", color: T.txt, fontSize: 11, outline: "none", fontFamily: "inherit", boxSizing: "border-box" }} />
                </div>
                <div style={{ flex: 1, overflowY: "auto", padding: "4px 16px 12px" }}>
                  {ALL_COLUMNS.filter(c => !c.required).filter(c => !colSearch || c.label.toLowerCase().includes(colSearch.toLowerCase())).map(col => {
                    const isActive = colEditorDraft.includes(col.id);
                    return (
                      <div key={col.id} onClick={() => {
                        if (isActive) setColEditorDraft(prev => prev.filter(c => c !== col.id));
                        else setColEditorDraft(prev => [...prev, col.id]);
                      }}
                        style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", marginBottom: 2, borderRadius: 8, cursor: "pointer", transition: "background 0.1s" }}
                        onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.04)"}
                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                        <div style={{ width: 20, height: 20, borderRadius: 8, background: isActive ? "#4059AD" : "transparent", border: isActive ? "1.5px solid #4059AD" : "1.5px solid rgba(217,219,221,0.12)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          {isActive && <span style={{ color: "#fff", fontSize: 11, fontWeight: 400 }}>✓</span>}
                        </div>
                        <span style={{ fontSize: 12, color: T.txt }}>{col.label}</span>
                      </div>
                    );
                  })}
                  {ALL_COLUMNS.filter(c => !c.required).filter(c => !colSearch || c.label.toLowerCase().includes(colSearch.toLowerCase())).length === 0 && (
                    <div style={{ padding: 20, textAlign: "center", fontSize: 11, color: T.tx3 }}>No fields match "{colSearch}"</div>
                  )}
                </div>
              </div>

              {/* Right: Selected columns */}
              <div style={{ width: "50%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
                <div style={{ padding: "12px 16px 8px", flexShrink: 0 }}>
                  <div style={{ fontSize: 10, fontWeight: 400, color: T.tx3, letterSpacing: ".06em" }}>SELECTED COLUMNS ({colEditorDraft.length})</div>
                  <div style={{ fontSize: 9, color: T.tx3, marginTop: 2 }}>Drag to reorder</div>
                </div>
                <div style={{ flex: 1, overflowY: "auto", padding: "4px 16px 12px" }}>
                  {colEditorDraft.map((colId, idx) => {
                    const col = ALL_COLUMNS.find(c => c.id === colId);
                    if (!col) return null;
                    return (
                      <div key={colId}
                        draggable
                        onDragStart={() => { dragItem.current = idx; }}
                        onDragEnter={() => { dragOver.current = idx; }}
                        onDragOver={e => e.preventDefault()}
                        onDragEnd={() => {
                          const from = dragItem.current;
                          const to = dragOver.current;
                          if (from === null || to === null || from === to) return;
                          const n = [...colEditorDraft];
                          const item = n.splice(from, 1)[0];
                          n.splice(to, 0, item);
                          setColEditorDraft(n);
                          dragItem.current = null;
                          dragOver.current = null;
                        }}
                        style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", marginBottom: 3, borderRadius: 8, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(217,219,221,0.08)", cursor: "grab", userSelect: "none" }}>
                        <span style={{ fontSize: 12, color: "rgba(255,255,255,0.2)", cursor: "grab", lineHeight: 1 }}>⠿</span>
                        <span style={{ flex: 1, fontSize: 12, fontWeight: 400, color: T.txt }}>{col.label}</span>
                        {!col.required && (
                          <button onClick={(e) => { e.stopPropagation(); setColEditorDraft(prev => prev.filter(c => c !== colId)); }}
                            style={{ width: 22, height: 22, borderRadius: 6, background: "rgba(255,255,255,0.08)", border: "1px solid rgba(217,219,221,0.12)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: T.tx3 }}>×</button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div style={{ display: "flex", gap: 6, justifyContent: "flex-end", padding: "12px 20px", borderTop: "1px solid rgba(217,219,221,0.08)", flexShrink: 0 }}>
              <button onClick={() => { setColEditorDraft([...DEFAULT_COLS]); }} style={{ padding: "8px 18px", borderRadius: 8, fontSize: 11, fontWeight: 400, letterSpacing: ".04em", textTransform: "uppercase", color: T.txt, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(217,219,221,0.12)", cursor: "pointer", fontFamily: "inherit" }}>Reset</button>
              <button onClick={() => setShowColEditor(false)} style={{ padding: "8px 18px", borderRadius: 8, fontSize: 11, fontWeight: 400, letterSpacing: ".04em", textTransform: "uppercase", color: T.txt, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(217,219,221,0.12)", cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
              <button onClick={() => { applyColumns([...colEditorDraft]); setShowColEditor(false); setColSearch(""); }} style={{ padding: "8px 18px", borderRadius: 8, fontSize: 11, fontWeight: 400, letterSpacing: ".04em", textTransform: "uppercase", color: "#D9DBDD", background: T.blue, border: "none", cursor: "pointer", fontFamily: "inherit" }}>Apply</button>
            </div>
          </div>
        </div>, document.body
      )}

      {/* ─── HEADER BAR ─── */}
      <div style={{ display: "flex", gap: 6, padding: isMobile ? "8px 12px 10px" : "10px 24px 14px", alignItems: "center", flexWrap: isMobile ? "wrap" : "nowrap" }}>
        <input value={search} onChange={e => { setSearch(e.target.value); setCurrentPage(1); }} placeholder="Search..."
          style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "rgba(0,0,0,0.15)", fontSize: 12, fontFamily: "inherit", outline: "none", color: "#FAFAFA", width: isMobile ? "100%" : 180 }} />
        {!isMobile && <div style={{ flex: 1 }} />}
        {queueMsg && <span style={{ fontSize: 10, color: T.grn, fontWeight: 400 }}>{queueMsg}</span>}

        {/* Bulk actions */}
        {selected.size > 0 && (
          <>
            <button onClick={() => addToQueue(selected)} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "transparent", color: "#FAFAFA", fontSize: 11, fontWeight: 400, cursor: "pointer", fontFamily: "inherit" }}>
              Add to Queue ({selected.size})
            </button>
            <button onClick={bulkDelete} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "transparent", color: "#FAFAFA", fontSize: 11, fontWeight: 400, cursor: "pointer", fontFamily: "inherit" }}>
              Delete ({selected.size})
            </button>
          </>
        )}
        <button onClick={() => { setColEditorDraft([...visibleCols]); setColSearch(""); setShowColEditor(true); }} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "transparent", color: "#FAFAFA", fontSize: 11, fontWeight: 400, cursor: "pointer", fontFamily: "inherit" }}>
          <Icon name="settings" size={12} color="#FAFAFA" /> Columns
        </button>
        <button onClick={() => setShowAddContactModal(true)} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "transparent", color: "#FAFAFA", fontSize: 11, fontWeight: 400, cursor: "pointer", fontFamily: "inherit" }}>+ Add Contact</button>
        {/* ─── LISTS MULTI-SELECT FILTER ─── */}
        <div ref={listsDropdownRef} style={{ position: "relative" }}>
          <button onClick={() => setListsDropdownOpen(o => !o)}
            style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: listsDropdownOpen ? "rgba(255,255,255,0.06)" : "transparent", color: "#FAFAFA", fontSize: 11, fontWeight: 400, cursor: "pointer", fontFamily: "inherit" }}>
            Lists <span style={{ fontSize: 9, opacity: 0.6 }}>▾</span>
          </button>
          {listsDropdownOpen && (
            <div style={{
              position: "absolute",
              top: "calc(100% + 4px)",
              right: 0,
              minWidth: 240,
              maxHeight: 360,
              overflowY: "auto",
              background: "#33363D",
              border: "1px solid rgba(217,219,221,0.12)",
              borderRadius: 8,
              boxShadow: "0 24px 64px rgba(0,0,0,0.55)",
              padding: 8,
              zIndex: 99999,
            }}>
              {allLists.length === 0 && (
                <div style={{ padding: 10, fontSize: 11, color: T.tx3, textAlign: "center" }}>No lists yet</div>
              )}
              {allLists.map(list => {
                const checked = selectedListIds.includes(list.id);
                return (
                  <label key={list.id}
                    style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 8px", cursor: "pointer", fontSize: 12, borderRadius: 6 }}
                    onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.04)"}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    <Checkbox checked={checked} onChange={() => {
                      if (checked) setSelectedListIds(selectedListIds.filter(id => id !== list.id));
                      else setSelectedListIds([...selectedListIds, list.id]);
                    }} />
                    <span style={{ flex: 1, color: "#FAFAFA" }}>{list.name}</span>
                    <span style={{ fontSize: 9, color: T.tx3, textTransform: "uppercase", letterSpacing: ".06em" }}>{list.type}</span>
                  </label>
                );
              })}
              {selectedListIds.length > 0 && (
                <button onClick={() => setSelectedListIds([])}
                  style={{ marginTop: 6, padding: "6px 8px", fontSize: 11, color: T.tx3, background: "none", border: "none", cursor: "pointer", width: "100%", textAlign: "left", fontFamily: "inherit" }}>
                  Clear all
                </button>
              )}
            </div>
          )}
        </div>
        {/* ─── DISPOSITION QUICK-FILTER CHIPS + MULTISELECT (commit D) ─── */}
        {/* Three quick-filter chips for the most common behavior queries
            plus a multiselect that lists every admin-configured disposition
            from store.callDispositions (custom dispositions appear here
            automatically without code changes). */}
        <button
          onClick={() => setShowDncOnly(!showDncOnly)}
          title="Filter to contacts marked Do Not Call"
          style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: "6px 14px", borderRadius: 8,
            border: "1px solid " + (showDncOnly ? "#E53935" : "rgba(217,219,221,0.12)"),
            background: showDncOnly ? "rgba(229,57,53,0.12)" : "transparent",
            color: showDncOnly ? "#E53935" : "#FAFAFA",
            fontSize: 11, fontWeight: 400, cursor: "pointer", fontFamily: "inherit",
          }}>DNC</button>
        <button
          onClick={() => setShowQualifiedOnly(!showQualifiedOnly)}
          title="Filter to contacts marked Qualified"
          style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: "6px 14px", borderRadius: 8,
            border: "1px solid " + (showQualifiedOnly ? "#2E8B57" : "rgba(217,219,221,0.12)"),
            background: showQualifiedOnly ? "rgba(46,139,87,0.12)" : "transparent",
            color: showQualifiedOnly ? "#2E8B57" : "#FAFAFA",
            fontSize: 11, fontWeight: 400, cursor: "pointer", fontFamily: "inherit",
          }}>Qualified</button>
        <button
          onClick={() => setShowRequeueOnly(!showRequeueOnly)}
          title="Filter to contacts awaiting re-queue (in cool-down window)"
          style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: "6px 14px", borderRadius: 8,
            border: "1px solid " + (showRequeueOnly ? "#D4763A" : "rgba(217,219,221,0.12)"),
            background: showRequeueOnly ? "rgba(212,118,58,0.12)" : "transparent",
            color: showRequeueOnly ? "#D4763A" : "#FAFAFA",
            fontSize: 11, fontWeight: 400, cursor: "pointer", fontFamily: "inherit",
          }}>Awaiting requeue</button>
        {/* Disposition multiselect — mirrors Lists dropdown above. Iterates
            store.callDispositions filtered to enabled !== false; custom
            admin-added dispositions appear automatically. */}
        <div ref={dispositionDropdownRef} style={{ position: "relative" }}>
          <button onClick={() => setDispositionDropdownOpen(o => !o)}
            style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: dispositionDropdownOpen ? "rgba(255,255,255,0.06)" : "transparent", color: "#FAFAFA", fontSize: 11, fontWeight: 400, cursor: "pointer", fontFamily: "inherit" }}>
            Disposition{selectedDispositionIds.length > 0 ? ` (${selectedDispositionIds.length})` : ""} <span style={{ fontSize: 9, opacity: 0.6 }}>▾</span>
          </button>
          {dispositionDropdownOpen && (() => {
            const enabled = (store.callDispositions || []).filter(d => d.enabled !== false);
            return (
              <div style={{
                position: "absolute",
                top: "calc(100% + 4px)",
                right: 0,
                minWidth: 240,
                maxHeight: 360,
                overflowY: "auto",
                background: "#33363D",
                border: "1px solid rgba(217,219,221,0.12)",
                borderRadius: 8,
                boxShadow: "0 24px 64px rgba(0,0,0,0.55)",
                padding: 8,
                zIndex: 99999,
              }}>
                {enabled.length === 0 && (
                  <div style={{ padding: 10, fontSize: 11, color: T.tx3, textAlign: "center" }}>No dispositions configured</div>
                )}
                {enabled.map(d => {
                  const checked = selectedDispositionIds.includes(d.id);
                  return (
                    <label key={d.id}
                      style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 8px", cursor: "pointer", fontSize: 12, borderRadius: 6 }}
                      onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.04)"}
                      onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                      <Checkbox checked={checked} onChange={() => {
                        if (checked) setSelectedDispositionIds(selectedDispositionIds.filter(id => id !== d.id));
                        else setSelectedDispositionIds([...selectedDispositionIds, d.id]);
                      }} />
                      <span style={{ flex: 1, color: "#FAFAFA" }}>{d.label}</span>
                    </label>
                  );
                })}
                {selectedDispositionIds.length > 0 && (
                  <button onClick={() => setSelectedDispositionIds([])}
                    style={{ marginTop: 6, padding: "6px 8px", fontSize: 11, color: T.tx3, background: "none", border: "none", cursor: "pointer", width: "100%", textAlign: "left", fontFamily: "inherit" }}>
                    Clear all
                  </button>
                )}
              </div>
            );
          })()}
        </div>
      </div>

      {/* ─── TABLE (List View) ─── */}
      {viewMode === "list" && isMobile && <div key={"view-" + viewKey} style={{ ...crd, overflowY: "auto", padding: "0 8px" }}>
        {paginatedContacts.map(c => (
          <div key={c.id} onClick={() => onSelectContact(c)} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 10px", borderBottom: "1px solid " + T.bdr, cursor: "pointer" }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: T.blue + "15", border: "1px solid " + T.blue + "25", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 400, color: T.blue, flexShrink: 0 }}>{(c.fn||"?")[0]}{(c.ln||"?")[0]}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 400, color: T.txt, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.fn} {c.ln}</div>
              <div style={{ fontSize: 10, color: T.tx3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.company || c.co || c.email || ""}</div>
            </div>
            <div style={{ textAlign: "right", flexShrink: 0 }}>
              <div style={{ fontSize: 11, fontWeight: 400, color: c.score >= 80 ? T.grn : c.score >= 50 ? T.amb : T.red }}>{c.score || "—"}</div>
              <div style={{ fontSize: 9, color: T.tx3, textTransform: "capitalize" }}>{c.status || ""}</div>
            </div>
          </div>
        ))}
        {filtered.length === 0 && <div style={{ padding: 40, textAlign: "center", color: T.tx3, fontSize: 12 }}>No contacts match</div>}
        {/* Mobile pagination */}
        {filtered.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "12px 0", gap: 12 }}>
            <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)} style={{ padding: "6px 14px", borderRadius: 8, border: "1px solid " + T.bdr, background: "transparent", color: T.txt, fontSize: 11, cursor: "pointer" }}>Prev</button>
            <span style={{ fontSize: 11, color: T.tx3 }}>{currentPage} / {Math.ceil(filtered.length / pageSize)}</span>
            <button disabled={currentPage >= Math.ceil(filtered.length / pageSize)} onClick={() => setCurrentPage(p => p + 1)} style={{ padding: "6px 14px", borderRadius: 8, border: "1px solid " + T.bdr, background: "transparent", color: T.txt, fontSize: 11, cursor: "pointer" }}>Next</button>
          </div>
        )}
      </div>}
      {viewMode === "list" && !isMobile && <div key={"view-" + viewKey} className="cf-page-enter" style={{ ...crd, overflowX: "auto", overflowY: "visible", padding: "0 24px" }}>
        <table style={{ width: "100%", minWidth: visibleCols.length > 6 ? visibleCols.length * 130 : "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
          <thead>
            <tr style={{ borderBottom: "1px solid " + T.bdr, position: "sticky", top: 0, background: "#2C2E33", zIndex: 2 }}>
              <th style={{ padding: "10px 12px", width: 36 }}>
                <Checkbox checked={allSelected} onChange={toggleAll} />
              </th>
              {renderCols.map(colId => {
                const col = ALL_COLUMNS.find(c => c.id === colId);
                // No `if (!col) return null` — renderCols is pre-filtered.
                const align = colId === "name" ? "left" : "center";
                return (
                  <th key={col.id} onClick={() => handleSort(col.id)}
                    style={{ padding: "10px 12px", textAlign: align, fontSize: 10, fontWeight: 400, color: T.tx3, letterSpacing: ".06em", textTransform: "uppercase", cursor: "pointer", width: col.w || "auto", whiteSpace: "nowrap", userSelect: "none" }}>
                    <span>{col.label}{sortCol === col.id && <span style={{ color: "#FAFAFA", fontSize: 11, marginLeft: 4 }}>{sortIcon(col.id)}</span>}</span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {paginatedContacts.map(c => (
              <tr key={c.id} style={{ borderBottom: "1px solid " + T.bdr, transition: "background 0.1s" }}
                onMouseEnter={e => e.currentTarget.style.background = T.bg}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                <td style={{ padding: "8px 12px" }} onClick={e => e.stopPropagation()}>
                  <Checkbox checked={selected.has(c.id)} onChange={() => toggleOne(c.id)} />
                </td>
                {renderCols.map(colId => {
                  const cellStyle = { padding: "8px 12px", cursor: "pointer", textAlign: colId === "name" ? "left" : "center" };
                  const click = () => onSelectContact(c);
                  // Per-cell width pulled from ALL_COLUMNS via colWidthById.
                  // Both `width` and `maxWidth` are set so fixed-layout honors
                  // the constraint and the truncation triple has something to
                  // clip against (overflow:hidden needs a bounded width).
                  const w = colWidthById[colId];
                  const wStyle = w ? { width: w, maxWidth: w } : {};
                  if (colId === "name") {
                    const fullName = `${c.fn || ""} ${c.ln || ""}`.trim();
                    return (
                      <td key={colId} style={{ ...cellStyle, ...truncStyle, ...wStyle, textAlign: "left" }} onClick={click} title={fullName}>
                        {/* Outer wrapper inherits width from <td>; inner divs each
                            clip independently so name + title-secondary both
                            ellipsize on their own line. */}
                        <div style={{ ...truncStyle }}>
                          <div style={{ fontSize: 12, fontWeight: 400, color: T.txt, ...truncStyle }}>{c.fn} {c.ln}</div>
                          <div style={{ fontSize: 9, color: T.tx3, ...truncStyle }} title={c.title || ""}>{c.title}</div>
                        </div>
                      </td>
                    );
                  }
                  if (colId === "company") { const v = c.company || c.co || "—"; return <td key={colId} style={{ ...cellStyle, ...truncStyle, ...wStyle, fontSize: 11, color: T.tx2 }} onClick={click} title={v}>{v}</td>; }
                  if (colId === "email") { const v = c.email || "—"; return <td key={colId} style={{ ...cellStyle, ...truncStyle, ...wStyle, fontSize: 11, color: T.tx2 }} onClick={click} title={v}>{v}</td>; }
                  if (colId === "phone") { const v = c.ph || c.phone || "—"; return <td key={colId} style={{ ...cellStyle, ...truncStyle, ...wStyle, fontSize: 11, color: T.tx2 }} onClick={click} title={v}>{v}</td>; }
                  if (colId === "title") { const v = c.title || "—"; return <td key={colId} style={{ ...cellStyle, ...truncStyle, ...wStyle, fontSize: 11, color: T.tx2 }} onClick={click} title={v}>{v}</td>; }
                  if (colId === "score") return (
                    <td key={colId} style={{ ...cellStyle, ...truncStyle, ...wStyle, fontSize: 11, fontWeight: 400, color: "#FAFAFA" }} onClick={click}>
                      {c.score || "—"}
                    </td>
                  );
                  if (colId === "status") return <td key={colId} style={{ ...cellStyle, ...truncStyle, ...wStyle, fontSize: 11, color: T.txt, textTransform: "capitalize" }}>{c.status || "—"}</td>;
                  if (colId === "source") return <td key={colId} style={{ ...cellStyle, ...truncStyle, ...wStyle, fontSize: 11, color: T.tx3 }}>{c.source || "—"}</td>;
                  if (colId === "last") return <td key={colId} style={{ ...cellStyle, ...truncStyle, ...wStyle, fontSize: 11, color: T.tx3 }}>{c.last || c.lastActivity || "—"}</td>;
                  if (colId === "owner") return <td key={colId} style={{ ...cellStyle, ...truncStyle, ...wStyle, fontSize: 11, color: T.tx3 }}>{store.getOwnerName(c.owner) || "—"}</td>;
                  if (colId === "created") return <td key={colId} style={{ ...cellStyle, ...truncStyle, ...wStyle, fontSize: 11, color: T.tx3 }}>{c.created ? cfDate(c.created) : "—"}</td>;
                  if (colId === "tags") { const v = (c.tags || []).join(", ") || "—"; return <td key={colId} style={{ ...cellStyle, ...truncStyle, ...wStyle, fontSize: 11, color: T.tx3 }} title={v}>{v}</td>; }
                  if (colId.startsWith("cf_")) {
                    const cfId = colId.slice(3);
                    const val = c.properties?.[cfId] || c[cfId] || "";
                    const display = val || "—";
                    return <td key={colId} style={{ ...cellStyle, ...truncStyle, ...wStyle, fontSize: 11, color: T.tx2 }} onClick={click} title={String(val)}>{display}</td>;
                  }
                  return <td key={colId} style={{ ...cellStyle, ...truncStyle, ...wStyle }}>—</td>;
                })}
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={renderCols.length + 1} style={{ padding: 0 }}><div style={{ padding: 40, textAlign: "center", color: T.tx3, fontSize: 12, position: "sticky", left: 0, width: "calc(100vw - 280px)" }}>No contacts match the current filters</div></td></tr>
            )}
          </tbody>
        </table>
        {/* ─── PAGINATION ─── */}
        {filtered.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0", flexShrink: 0, borderTop: "1px solid rgba(217,219,221,0.06)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: 11, color: T.tx3 }}>{((currentPage - 1) * pageSize) + 1}–{Math.min(currentPage * pageSize, filtered.length)} of {filtered.length}</span>
              <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
                {[20, 50, 100].map(size => (
                  <button key={size} onClick={() => { setPageSize(size); setCurrentPage(1); }} style={{
                    padding: "2px 6px", borderRadius: 4, fontSize: 10, border: "none",
                    background: pageSize === size ? "rgba(64,89,173,0.15)" : "transparent",
                    color: pageSize === size ? "#FAFAFA" : T.tx3, cursor: "pointer", fontFamily: "inherit",
                  }}>{size}</button>
                ))}
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
              <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} style={{ padding: "4px 8px", borderRadius: 4, border: "none", background: "transparent", color: currentPage === 1 ? "rgba(250,250,250,0.15)" : "#FAFAFA", cursor: currentPage === 1 ? "default" : "pointer", fontFamily: "inherit", fontSize: 12 }}>‹</button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pn; if (totalPages <= 5) pn = i + 1; else if (currentPage <= 3) pn = i + 1; else if (currentPage >= totalPages - 2) pn = totalPages - 4 + i; else pn = currentPage - 2 + i;
                return (
                  <button key={pn} onClick={() => setCurrentPage(pn)} style={{
                    width: 28, height: 28, borderRadius: 6, fontSize: 10, border: "none", fontFamily: "inherit",
                    background: currentPage === pn ? "rgba(64,89,173,0.15)" : "transparent",
                    color: currentPage === pn ? "#FAFAFA" : T.tx3, cursor: "pointer",
                  }}>{pn}</button>
                );
              })}
              <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} style={{ padding: "4px 8px", borderRadius: 4, border: "none", background: "transparent", color: currentPage === totalPages ? "rgba(250,250,250,0.15)" : "#FAFAFA", cursor: currentPage === totalPages ? "default" : "pointer", fontFamily: "inherit", fontSize: 12 }}>›</button>
            </div>
          </div>
        )}
      </div>}

      {/* ─── BOARD VIEW (Kanban) ─── */}
      {viewMode === "board" && (
        <div style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 240px)" }}>
          {/* Chevron stage header */}
          <div style={{ display: "flex", flexShrink: 0, height: 36, marginBottom: 12 }}>
            {(store.leadStages || []).map((stage, i) => {
              const isLast = i === (store.leadStages || []).length - 1;
              const count = filtered.filter(c => { const cs = (c.leadStage || c.lead_stage || "new").toLowerCase(); return cs === stage.name.toLowerCase() || cs === String(stage.id); }).length;
              return (
                <div key={stage.id} style={{ flex: 1, position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <svg viewBox="0 0 200 36" preserveAspectRatio="none" style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}>
                    <path d={i === 0 ? "M0,0 L186,0 L200,18 L186,36 L0,36 Z" : isLast ? "M0,0 L200,0 L200,36 L0,36 L14,18 Z" : "M0,0 L186,0 L200,18 L186,36 L0,36 L14,18 Z"} fill="#2C2E33" stroke="rgba(217,219,221,0.25)" strokeWidth="1" />
                  </svg>
                  <div style={{ position: "relative", zIndex: 1, display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontSize: 11, fontWeight: 400, color: "#FAFAFA" }}>{stage.name}</span>
                    <span style={{ fontSize: 9, color: "rgba(250,250,250,0.45)" }}>{count}</span>
                  </div>
                </div>
              );
            })}
          </div>
          {/* Drop columns */}
          <div style={{ display: "flex", flex: 1, gap: 0, overflow: "hidden" }}>
            {(store.leadStages || []).map(stage => {
              const stageContacts = filtered.filter(c => {
                const cs = (c.leadStage || c.lead_stage || "new").toLowerCase();
                return cs === stage.name.toLowerCase() || cs === String(stage.id);
              });
              return (
                <div key={stage.id}
                  onDragOver={e => { e.preventDefault(); e.currentTarget.style.background = "rgba(64,89,173,0.06)"; }}
                  onDragLeave={e => { e.currentTarget.style.background = "transparent"; }}
                  onDrop={e => {
                    e.currentTarget.style.background = "transparent";
                    const contactId = e.dataTransfer.getData("contactId");
                    if (contactId) store.updateContact(contactId, { leadStage: stage.name.toLowerCase() });
                  }}
                  style={{ flex: 1, display: "flex", flexDirection: "column", borderRight: "1px solid rgba(217,219,221,0.06)", overflowY: "auto", padding: "6px 6px", transition: "background 0.15s" }}>
                  {stageContacts.map(c => (
                    <div key={c.id}
                      draggable
                      onDragStart={e => e.dataTransfer.setData("contactId", c.id)}
                      onClick={() => onSelectContact(c)}
                      style={{ padding: "10px 12px", marginBottom: 4, borderRadius: 8, background: "#2C2E33", border: "1px solid rgba(217,219,221,0.10)", cursor: "grab", userSelect: "none" }}
                      onMouseEnter={e => e.currentTarget.style.borderColor = "rgba(217,219,221,0.25)"}
                      onMouseLeave={e => e.currentTarget.style.borderColor = "rgba(217,219,221,0.10)"}>
                      <div style={{ fontSize: 12, fontWeight: 400, color: T.txt, marginBottom: 2 }}>{c.fn} {c.ln}</div>
                      {(c.company || c.co) && <div style={{ fontSize: 10, color: T.tx3, marginBottom: 4 }}>{c.company || c.co}</div>}
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        {c.score > 0 && <span style={{ fontSize: 10, fontWeight: 400, color: "#FAFAFA" }}>{c.score}</span>}
                        {c.source && <span style={{ fontSize: 9, color: T.tx3 }}>{c.source}</span>}
                      </div>
                    </div>
                  ))}
                  {stageContacts.length === 0 && (
                    <div style={{ padding: "20px 10px", textAlign: "center", fontSize: 11, color: T.tx3, opacity: 0.3 }}>Drop here</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      </div>

      <AddContactModal
        isOpen={showAddContactModal}
        onClose={() => setShowAddContactModal(false)}
      />
    </div>
  );
}
